/**
 * 작업 탭 — 편집기 자리를 함께 쓰는 화면들(2026-10-19 사용자 요구).
 *
 * 「SQL 편집」(편집기 + 결과 + 편집기 툴바 + 이력·분석 패널)이 첫 탭이고 닫히지 않는다. 메뉴에서 여는 큰 화면들
 * (SQL 작성·튜닝·품질 검사·PL/SQL 편집기·모니터링 …)은 팝업이 아니라 **그 자리에 탭으로** 열리고, 여러 개를 열어 두고 오간다.
 *
 * 구현은 창 쪽을 고치지 않는다 — 「창처럼」(windowed) 창은 이미 화면을 포털 그릇(useModalWindow 의 holder)에 그리고 그 DOM 노드만
 * 옮긴다(분리·되돌리기). 작업 탭은 그 그릇을 **이 영역의 칸(slot)으로** 옮길 뿐이라 창의 열고 닫는 상태·내용은 그대로다.
 * 숨긴 탭도 마운트된 채 둔다(입력·조회 결과가 남는다). AI 에이전트는 탭이 아니라 오른쪽 패널이다(모든 탭에서 보인다).
 *
 * 창(useModalWindow)이 열리면 register → 그 탭이 앞에 온다. 메뉴에서 이미 열린 창을 다시 고르면 open 이 바뀌지 않아 다시 알릴 길이
 * 없으므로, 메뉴는 activateByTitle 로 그 탭을 앞으로 부른다(창 제목 = 메뉴 문구 — 같은 번역 키를 쓴다, docs/ui-modal-consistency.md).
 *
 * 창 오가기(2026-11-03 시험 피드백) — Alt+← 직전 창 · Alt+→ 다음 창. 브라우저의 뒤로/앞으로처럼 **본 순서의 기록**을 따라간다
 * (탭 줄의 순서가 아니다 — 두 창을 번갈아 볼 때 Alt+← · Alt+→ 로 오간다). 닫은 창은 기록에서 빠진다.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiX, FiEdit3, FiExternalLink, FiCornerDownLeft } from 'react-icons/fi';
import './WorkspaceDock.css';

export const SQL_TAB = 'sql';
/** 메뉴 문구 끝의 「...」 를 뗀 창 제목 — 메뉴 「DB 연결 관리...」 와 탭 「DB 연결 관리」 를 같은 화면으로 본다(2026-11-04) */
export const menuTitle = (s) => (typeof s === 'string' ? s.replace(/\s*(\.\.\.|…)\s*$/, '') : s);
const sameTitle = (a, b) => !!a && !!b && menuTitle(a) === menuTitle(b);
const ACTIVE_KEY = 'livesql.workspace.activeTitle';
/** 기동 직후 창 복원(workspaceLayout RESTORED_WINDOWS)이 여는 창들은 앞으로 부르지 않는다 — 마지막에 보던 탭만 */
const RESTORE_MS = 2500;
/** 창 오가기 기록의 길이 */
const NAV_MAX = 30;

/** 본 순서의 기록 — 순수 함수(테스트용). nav = { list: [id…], idx } */
export function navPush(nav, id) {
    if (nav.list[nav.idx] === id) return nav;
    const list = [...nav.list.slice(0, nav.idx + 1), id].slice(-NAV_MAX);
    return { list, idx: list.length - 1 };
}
/** dir = -1(직전) | 1(다음). 열려 있지 않은 창은 건너뛴다. 갈 곳이 없으면 null */
export function navStep(nav, dir, isOpen) {
    for (let i = nav.idx + dir; i >= 0 && i < nav.list.length; i += dir) {
        if (isOpen(nav.list[i])) return { nav: { ...nav, idx: i }, id: nav.list[i] };
    }
    return null;
}
/** 닫은 창을 기록에서 빼고, 이어진 같은 항목은 하나로 */
export function navRemove(nav, id) {
    const list = [];
    let idx = 0;
    nav.list.forEach((x, i) => {
        if (x !== id && list[list.length - 1] !== x) list.push(x);
        if (i <= nav.idx) idx = Math.max(0, list.length - 1);
    });
    return { list, idx: Math.min(idx, Math.max(0, list.length - 1)) };
}

const readSaved = () => { try { return localStorage.getItem(ACTIVE_KEY) || ''; } catch (_e) { return ''; } };
const writeSaved = (v) => { try { if (v) localStorage.setItem(ACTIVE_KEY, v); else localStorage.removeItem(ACTIVE_KEY); } catch (_e) { /* 저장 안 됨 — 다음 기동은 SQL 편집 */ } };

const WorkspaceDockContext = createContext(null);
export const useWorkspaceDock = () => useContext(WorkspaceDockContext);
/**
 * 창(useModalWindow)이 쓰는 부분만 — 칸(slots)과 올리기·내리기. **앞에 온 탭(activeId)·탭 목록이 바뀌어도 바뀌지 않는다**(2026-10-26):
 * 예전에는 창마다 전체 문맥을 읽어 탭을 오갈 때마다 열린 창이 전부 다시 그려졌다(탭 전환이 멈칫했다)
 */
const WorkspaceDockApiContext = createContext(null);
export const useWorkspaceDockApi = () => useContext(WorkspaceDockApiContext);

export function WorkspaceDockProvider({ children }) {
    const [views, setViews] = useState([]);          // [{ id, title, icon, popped, withResult }]
    const [activeId, setActiveId] = useState(SQL_TAB);
    const [slots, setSlots] = useState({});          // id → 칸 DOM
    const actions = useRef({});                      // id → { close, dockBack } — 렌더마다 바뀌어도 탭을 다시 그리지 않게
    const startedAt = useRef(Date.now());
    const savedTitle = useRef(readSaved());
    const viewsRef = useRef(views);
    viewsRef.current = views;
    const activeRef = useRef(activeId);
    activeRef.current = activeId;
    // 창 오가기 기록 — 앞에 온 탭이 바뀔 때마다 쌓는다(기록을 따라 움직인 것은 쌓지 않는다)
    const navRef = useRef({ list: [SQL_TAB], idx: 0 });
    const navMoving = useRef(false);
    useEffect(() => {
        if (navMoving.current) { navMoving.current = false; return; }
        navRef.current = navPush(navRef.current, activeId);
    }, [activeId]);

    const activate = useCallback((id) => {
        setActiveId(id);
        const v = viewsRef.current.find(x => x.id === id);
        writeSaved(v ? v.title : '');
    }, []);

    /** 사용자가 고른 것이 아닌 앞으로 부르기(편집기에 SQL 이 들어갔을 때 등) — 기동 직후 복원 중이거나 이미 앞이면 아무것도 안 한다 */
    const activateSoft = useCallback((id) => {
        if (activeRef.current === id || Date.now() - startedAt.current < RESTORE_MS) return;
        activate(id);
    }, [activate]);

    const register = useCallback((id, meta, acts) => {
        actions.current[id] = acts;
        const isNew = !viewsRef.current.some(v => v.id === id);
        setViews(prev => {
            const i = prev.findIndex(v => v.id === id);
            if (i < 0) return [...prev, { id, ...meta }];
            const cur = prev[i];
            if (cur.title === meta.title && cur.popped === meta.popped && cur.withResult === meta.withResult) return prev;
            const next = prev.slice(); next[i] = { ...cur, title: meta.title, popped: meta.popped, withResult: meta.withResult }; return next;
        });
        if (!isNew) return;
        const restoring = Date.now() - startedAt.current < RESTORE_MS;
        if (!restoring || (savedTitle.current && savedTitle.current === meta.title)) {
            setActiveId(id);
            if (!restoring) writeSaved(meta.title);
        }
    }, []);

    const unregister = useCallback((id) => {
        delete actions.current[id];
        navRef.current = navRemove(navRef.current, id);
        const rest = viewsRef.current.filter(v => v.id !== id);
        const last = rest[rest.length - 1];
        setViews(prev => prev.filter(v => v.id !== id));
        // 닫은 탭이 앞에 있었으면 마지막으로 연 탭(없으면 SQL 편집)이 앞으로
        if (activeRef.current === id) {
            setActiveId(last ? last.id : SQL_TAB);
            writeSaved(last ? last.title : '');
        }
    }, []);

    const activateByTitle = useCallback((title) => {
        if (!title) return false;
        const v = viewsRef.current.find(x => sameTitle(x.title, title));
        if (v) activate(v.id);
        return !!v;
    }, [activate]);

    const setSlot = useCallback((id, el) => {
        setSlots(prev => (prev[id] === el ? prev : (el ? { ...prev, [id]: el } : (() => { const n = { ...prev }; delete n[id]; return n; })())));
    }, []);

    const close = useCallback((id) => { const a = actions.current[id]; if (a && a.close) a.close(); }, []);

    // Alt+← 직전 창 · Alt+→ 다음 창 — 어디에 포커스가 있든(캡처 단계). 브라우저의 Alt+← 뒤로 가기도 막는다(앱을 떠나 버린다)
    const navigate = useCallback((dir) => {
        const isOpen = (id) => id === SQL_TAB || viewsRef.current.some(v => v.id === id);
        const r = navStep(navRef.current, dir, isOpen);
        if (!r) return false;
        navRef.current = r.nav;
        if (r.id !== activeRef.current) { navMoving.current = true; activate(r.id); }
        return true;
    }, [activate]);
    useEffect(() => {
        const onKey = (e) => {
            if (!e.altKey || e.ctrlKey || e.shiftKey || e.metaKey) return;
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
            e.preventDefault();
            e.stopPropagation();
            navigate(e.key === 'ArrowLeft' ? -1 : 1);
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [navigate]);
    const dockBack = useCallback((id) => { const a = actions.current[id]; if (a && a.dockBack) a.dockBack(); }, []);

    const api = useMemo(() => ({
        slots, activate, activateSoft, register, unregister, activateByTitle, setSlot, close, dockBack,
    }), [slots, activate, activateSoft, register, unregister, activateByTitle, setSlot, close, dockBack]);
    const value = useMemo(() => ({ ...api, views, activeId }), [api, views, activeId]);
    return (
        <WorkspaceDockApiContext.Provider value={api}>
            <WorkspaceDockContext.Provider value={value}>{children}</WorkspaceDockContext.Provider>
        </WorkspaceDockApiContext.Provider>
    );
}
WorkspaceDockProvider.propTypes = { children: PropTypes.node };


/** 작업 탭 줄 — 「SQL 편집」 + 열린 화면들. 모양은 툴바 단추와 같다(사용자 요청 2026-10-19 — 탭 모양 대신 단추) */
/**
 * 단추 줄 — 「SQL 편집」 이 늘 왼쪽 끝(사용자 요청 2026-10-19). **열린 것은 그 오른편(왼쪽 묶음), 닫힌 단추는 줄의 오른쪽 끝
 * AI 에이전트 바로 왼편**에 모은다(2026-10-23 사용자 요청).
 * buttons: [{ key, title?, node, open? }] — 보일 것만 넘긴다(즐겨찾기거나 실행 중인 것). title 이 열린 화면의 제목과 같으면
 * 단추 대신 그 화면의 탭(눌림·✕)을 왼쪽 묶음에 그린다. open 은 창이 아닌 켜진 패널(분석 모드·실행이력 등).
 * 단추가 없는 열린 화면(즐겨찾기가 아닌 메뉴로 연 것)은 왼쪽 묶음 끝에 붙는다.
 */
export function WorkspaceTabs({ buttons = [], trailing = null, onSqlEdit, sqlEditShown = true }) {
    const { t } = useTranslation();
    const ws = useWorkspaceDock();
    if (!ws) return null;
    // 「SQL 편집」 은 편집기 칸을 대신하던 화면(데이터 분석 등)도 걷어 편집기로 돌아간다 — onSqlEdit(App)
    const go = (id) => { ws.activate(id); if (id === SQL_TAB && onSqlEdit) onSqlEdit(); };
    // 눌린 모양은 **지금 보이는 화면 하나**에만 — 「SQL 편집」 은 편집기를 다른 화면(데이터 분석 등)이 덮고 있으면 눌리지 않는다(sqlEditShown)
    const isOn = (id) => ws.activeId === id && (id !== SQL_TAB || sqlEditShown);
    const tab = (id, label, icon, closable, popped) => (
        <div key={id} role="tab" tabIndex={0} aria-selected={isOn(id)}
            className={`detach-inline-btn ws-tab${isOn(id) ? ' active' : ''}${popped ? ' popped' : ''}`}
            onClick={() => go(id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(id); } }}
            onMouseDown={(e) => { if (closable && e.button === 1) { e.preventDefault(); ws.close(id); } }}
            title={popped ? t('common:ws.poppedHint', { name: label }) : label}>
            <span className="ws-tab-icon">{icon}</span>
            <span className="ws-tab-label">{label}</span>
            {popped && <FiExternalLink size={11} className="ws-tab-popped" />}
            {closable && (
                <button type="button" className="ws-tab-close" aria-label={t('common:ws.closeTab', { name: label })} title={t('common:ui.close')}
                    onClick={(e) => { e.stopPropagation(); ws.close(id); }}>
                    <FiX size={12} />
                </button>
            )}
        </div>
    );
    const items = buttons.map(btn => ({ btn, v: btn.title ? ws.views.find(x => sameTitle(x.title, btn.title)) : null }));
    const opened = items.filter(({ btn, v }) => v || btn.open);
    const closed = items.filter(({ btn, v }) => !v && !btn.open);
    return (
        <div className="ws-tabs" role="tablist" aria-label={t('common:ws.tabsLabel')}>
            {tab(SQL_TAB, t('common:ws.sqlEdit'), <FiEdit3 size={13} />, false, false)}
            {/* **열린 것은 왼쪽**(SQL 편집 오른편), **닫힌 단추는 오른쪽** AI 에이전트 바로 왼편(2026-10-23 사용자 요청).
                열린 것 = 작업 탭으로 열린 창, 켜진 패널(btn.open — 분석 모드·실행계획 분석·실행이력·쿼리 챌린지) */}
            {opened.map(({ btn, v }) => (v
                // 창이 아이콘을 안 줬으면 그 단추(메뉴)의 아이콘을 쓴다 — 열자마자 아이콘이 사라져 보였다
                ? tab(v.id, v.title, v.icon || btn.icon, true, v.popped)
                : <React.Fragment key={btn.key}>{btn.node}</React.Fragment>))}
            {ws.views.filter(v => !buttons.some(btn => sameTitle(btn.title, v.title))).map(v => tab(v.id, v.title, v.icon, true, v.popped))}
            {(closed.length > 0 || trailing) && (
                <span className="ws-tabs-right">
                    {closed.map(({ btn }) => <React.Fragment key={btn.key}>{btn.node}</React.Fragment>)}
                    {/* 늘 맨 오른쪽 끝(AI 에이전트 — 사용자 요청 2026-10-19) */}
                    {trailing ? <span className="ws-tabs-trailing">{trailing}</span> : null}
                </span>
            )}
        </div>
    );
}

WorkspaceTabs.propTypes = {
    buttons: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, title: PropTypes.string, icon: PropTypes.node, node: PropTypes.node, open: PropTypes.bool })),
    trailing: PropTypes.node,
    onSqlEdit: PropTypes.func,
    /** 편집기가 실제로 보이는가 — 아니면 「SQL 편집」 을 눌린 모양으로 두지 않는다 */
    sqlEditShown: PropTypes.bool,
};

/** 열린 화면들의 칸 — 앞에 온 탭만 보인다. 나머지도 마운트된 채 숨긴다 */
export function WorkspaceDockArea() {
    const { t } = useTranslation();
    const ws = useWorkspaceDock();
    const refs = useRef({});
    // 칸 ref 는 id 마다 고정 함수 — 렌더마다 새 함수면 React 가 null → el 을 다시 불러 그릇이 들락거린다
    const refFor = (id) => {
        if (!refs.current[id]) refs.current[id] = (el) => ws.setSlot(id, el);
        return refs.current[id];
    };
    useEffect(() => {
        const ids = new Set(ws ? ws.views.map(v => v.id) : []);
        Object.keys(refs.current).forEach(id => { if (!ids.has(id)) delete refs.current[id]; });
    }, [ws]);
    if (!ws) return null;
    const visible = ws.activeId !== SQL_TAB;
    return (
        <div className="ws-dock-area" style={visible ? undefined : { display: 'none' }}>
            {ws.views.map(v => (
                // 뒤로 간 칸은 display:none 이 아니라 크기를 지닌 채 숨긴다(.ws-dock-slot-hidden, 2026-10-22) — 크기가 0 이 됐다
                // 돌아오면 그래프가 모두 다시 재고 다시 그려 화면을 오갈 때마다 멈칫했다
                <div key={v.id} className={ws.activeId === v.id ? 'ws-dock-slot' : 'ws-dock-slot ws-dock-slot-hidden'}
                    aria-hidden={ws.activeId === v.id ? undefined : true}>
                    {v.popped && (
                        <div className="ws-dock-popped">
                            <FiExternalLink /> {t('common:ws.popped', { name: v.title })}
                            <button type="button" className="app-btn" onClick={() => ws.dockBack(v.id)}>
                                <FiCornerDownLeft size={13} /> {t('common:win.dockBack')}
                            </button>
                        </div>
                    )}
                    <div ref={refFor(v.id)} className="ws-dock-slot-body" />
                </div>
            ))}
        </div>
    );
}
